/**
 * MessagingAdmin — tenant-facing SMS notifications admin page.
 *
 * Three tabs: Settings · Test Tools · Logs.
 * Mirrors the ShippingAdmin pattern. Credentials NEVER appear in DOM;
 * the API surface returns only `credentials_set: true/false` plus the
 * last-4 of the Account SID.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { MessageSquare, Settings, FlaskConical, List } from "lucide-react";

const PROVIDER_LABELS = { mock: "Mock (development)", twilio: "Twilio" };

// Contact Validation & Messaging Configuration Recovery — Phase 10.
// The backend now returns a structured `{detail: {code, message}}`
// body for known failure modes (missing encryption key, invalid
// format, auth failure) instead of an unhandled 500. Prefer that
// human-readable message; fall back to whatever axios/the network
// layer gives us so a genuinely unexpected failure still shows
// SOMETHING rather than silently swallowing the error.
function describeError(e) {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (typeof detail === "string" && detail) return detail;
  return e?.message || String(e);
}

// General E.164 shape — not US-restricted, since a Twilio sender
// number/Messaging Service SID identity may legitimately be
// international. Mirrors the backend's routes/messaging/config.py
// `_E164_RE`.
const E164_RE = /^\+?[1-9]\d{1,14}$/;

export default function MessagingAdmin() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credsOpen, setCredsOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [mssid, setMssid] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  // Test SMS form
  const [provider, setProvider] = useState("mock");
  const [toPhone, setToPhone] = useState("");
  const [body, setBody] = useState("Test SMS from Rudras AI messaging.");
  const [eventType, setEventType] = useState("order_placed");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Logs
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messaging/config");
      setConfig(data);
      if (data?.default_provider) setProvider(data.default_provider);
    } catch (e) { toast.error(`Load config failed: ${describeError(e)}`); }
    finally { setLoading(false); }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get("/messaging/logs");
      setLogs(data?.logs || []);
    } catch (e) { toast.error(`Load logs failed: ${describeError(e)}`); }
    finally { setLoadingLogs(false); }
  };

  useEffect(() => { loadConfig(); }, []);

  const [senderPhoneError, setSenderPhoneError] = useState("");

  const saveSender = async (phone) => {
    const trimmed = (phone || "").trim();
    if (trimmed && !E164_RE.test(trimmed)) {
      setSenderPhoneError("Enter a valid E.164 phone number (e.g. +14155551234).");
      return;
    }
    setSenderPhoneError("");
    try {
      const { data } = await api.put("/messaging/config", { sender_phone_e164: trimmed || null });
      setConfig(data);
      toast.success("Sender phone saved");
    } catch (e) { toast.error(`Save failed: ${describeError(e)}`); }
  };

  const setProviderEnabled = async (name, enabled) => {
    try {
      const { data } = await api.put("/messaging/config", {
        providers: { [name]: { enabled } },
      });
      setConfig(data);
    } catch (e) { toast.error(`Toggle failed: ${describeError(e)}`); }
  };

  const setDefault = async (name) => {
    try {
      const { data } = await api.put("/messaging/config", { default_provider: name });
      setConfig(data); setProvider(name);
    } catch (e) { toast.error(`Set default failed: ${describeError(e)}`); }
  };

  const setBodiesFlag = async (val) => {
    try {
      const { data } = await api.put("/messaging/config", { log_message_bodies: val });
      setConfig(data);
    } catch (e) { toast.error(`Toggle failed: ${describeError(e)}`); }
  };

  const saveCreds = async () => {
    setSavingCreds(true);
    try {
      const creds = {};
      if (sid) creds.account_sid = sid;
      if (token) creds.auth_token = token;
      if (mssid) creds.messaging_service_sid = mssid;
      if (Object.keys(creds).length === 0) { toast.message("Nothing to save"); setSavingCreds(false); return; }
      const { data } = await api.put("/messaging/config", { credentials: { twilio: creds } });
      setConfig(data);
      setSid(""); setToken(""); setMssid("");
      setCredsOpen(false);
      toast.success("Twilio credentials saved (encrypted at rest)");
    } catch (e) { toast.error(`Save failed: ${describeError(e)}`); }
    finally { setSavingCreds(false); }
  };

  const validate = async (name) => {
    setValidating(true); setValidation(null);
    try {
      const { data } = await api.post("/messaging/config/validate", { provider: name });
      setValidation(data);
      if (data?.ok) toast.success(`${name} credentials validated`);
      else toast.error(`${name} validation failed: ${data?.detail || data?.error || "unknown"}`);
    } catch (e) { toast.error(`Validation error: ${describeError(e)}`); }
    finally { setValidating(false); }
  };

  const sendTest = async () => {
    setSending(true); setLastResult(null);
    try {
      const { data } = await api.post("/messaging/test-sms", {
        provider, to_phone_e164: toPhone, body, event_type: eventType,
      });
      setLastResult(data);
      if (data?.ok) toast.success(`Sent · ${data?.result?.provider_message_id || "(no id)"}`);
      else toast.error(`Send failed: ${data?.result?.error_message || data?.error?.detail || "unknown"}`);
    } catch (e) { toast.error(`Send error: ${describeError(e)}`); }
    finally { setSending(false); }
  };

  const twilio = config?.providers?.twilio;
  const credsSet = !!twilio?.credentials_set;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="messaging-admin-page">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-semibold">Messaging</h1>
            <p className="text-sm text-gray-500">SMS notifications · Mock + Twilio</p>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <Tabs defaultValue="settings">
            <TabsList>
              <TabsTrigger value="settings" data-testid="messaging-tab-settings"><Settings className="w-4 h-4 mr-1" /> Settings</TabsTrigger>
              <TabsTrigger value="tools" data-testid="messaging-tab-tools"><FlaskConical className="w-4 h-4 mr-1" /> Test Tools</TabsTrigger>
              <TabsTrigger value="logs" onClick={loadLogs} data-testid="messaging-tab-logs"><List className="w-4 h-4 mr-1" /> Logs</TabsTrigger>
            </TabsList>

            {/* ---------------- Settings ---------------- */}
            <TabsContent value="settings" className="space-y-6 pt-4">
              {config?.encryption_configured === false && (
                <div
                  className="border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-4 text-sm"
                  role="alert" data-testid="messaging-encryption-not-configured-banner"
                >
                  <div className="font-medium">Messaging credential storage is not configured for this environment.</div>
                  <div className="text-xs mt-1 text-amber-800">
                    Twilio credentials can't be saved until a platform operator configures messaging encryption for this environment. Existing settings (sender phone, provider toggles) are unaffected.
                  </div>
                </div>
              )}
              <div className="border rounded-lg p-4 bg-white space-y-3">
                <div className="font-medium">Sender phone (E.164)</div>
                <div className="flex gap-2">
                  <Input
                    data-testid="messaging-sender-phone"
                    placeholder="+14155551234"
                    defaultValue={config?.sender_phone_e164 || ""}
                    onBlur={(e) => e.target.value !== (config?.sender_phone_e164 || "") && saveSender(e.target.value)}
                  />
                </div>
                {senderPhoneError && (
                  <p className="text-xs text-rose-600" role="alert" data-testid="messaging-sender-phone-error">
                    {senderPhoneError}
                  </p>
                )}
                <div className="text-xs text-gray-500">
                  Outbound SMS will appear from this number. Twilio also supports a Messaging Service SID — set it in the Credentials Dialog.
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-white space-y-3">
                <div className="font-medium">Privacy</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm">Log SMS message bodies</div>
                    <div className="text-xs text-gray-500">Off by default. When on, the first 160 chars of each sent SMS is stored in the log.</div>
                  </div>
                  <Switch
                    data-testid="messaging-log-bodies-toggle"
                    checked={!!config?.log_message_bodies}
                    onCheckedChange={setBodiesFlag}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {["mock", "twilio"].map((name) => {
                  const p = config?.providers?.[name] || {};
                  return (
                    <div key={name} className="border rounded-lg p-4 bg-white" data-testid={`messaging-provider-card-${name}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{PROVIDER_LABELS[name] || name}</div>
                          <div className="text-xs text-gray-500">
                            {name === "twilio"
                              ? (p.credentials_set ? `credentials set${p.last4_sid ? ` · SID …${p.last4_sid}` : ""}` : "credentials not set")
                              : "no credentials required"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            data-testid={`messaging-provider-${name}-enabled`}
                            checked={!!p.enabled}
                            onCheckedChange={(v) => setProviderEnabled(name, v)}
                          />
                          <Button
                            size="sm" variant="outline"
                            data-testid={`messaging-set-default-${name}`}
                            onClick={() => setDefault(name)}
                          >
                            {config?.default_provider === name ? "Default" : "Make default"}
                          </Button>
                          {name === "twilio" && (
                            <Dialog open={credsOpen} onOpenChange={setCredsOpen}>
                              <DialogTrigger asChild>
                                <Button size="sm" data-testid="messaging-twilio-creds-button">
                                  {credsSet ? "Update" : "Add credentials"}
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader><DialogTitle>Twilio credentials</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                  <div>
                                    <Label>Account SID</Label>
                                    <Input
                                      data-testid="messaging-input-sid"
                                      type="text"
                                      placeholder="AC…"
                                      value={sid} onChange={(e) => setSid(e.target.value)}
                                    />
                                  </div>
                                  <div>
                                    <Label>Auth Token</Label>
                                    <Input
                                      data-testid="messaging-input-token"
                                      type="password"
                                      placeholder="••••••••"
                                      autoComplete="new-password"
                                      value={token} onChange={(e) => setToken(e.target.value)}
                                    />
                                    <div className="text-[11px] text-gray-500 mt-1">Stored encrypted (AES-GCM). Never displayed back.</div>
                                  </div>
                                  <div>
                                    <Label>Messaging Service SID (optional)</Label>
                                    <Input
                                      data-testid="messaging-input-mssid"
                                      type="text"
                                      placeholder="MG…"
                                      value={mssid} onChange={(e) => setMssid(e.target.value)}
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setCredsOpen(false)}>Cancel</Button>
                                  <Button
                                    data-testid="messaging-creds-save"
                                    disabled={savingCreds}
                                    onClick={saveCreds}
                                  >{savingCreds ? "Saving…" : "Save & encrypt"}</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                          <Button
                            size="sm" variant="ghost" disabled={validating}
                            data-testid={`messaging-validate-${name}`}
                            onClick={() => validate(name)}
                          >Validate</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {validation && (
                  <div
                    className={`text-xs rounded-lg p-3 ${validation.ok ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`}
                    data-testid="messaging-validation-result"
                  >
                    <div className="font-semibold">{validation.ok ? "Validated" : "Validation failed"}</div>
                    <div>{validation.detail || validation.error || (validation.ok ? "Provider is ready." : "Unknown error.")}</div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ---------------- Tools ---------------- */}
            <TabsContent value="tools" className="space-y-4 pt-4">
              <div className="border rounded-lg p-4 bg-white space-y-3">
                <div className="font-medium flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Send a test SMS</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Provider</Label>
                    <select
                      data-testid="messaging-test-provider"
                      className="w-full h-9 border rounded px-2 text-sm"
                      value={provider} onChange={(e) => setProvider(e.target.value)}
                    >
                      <option value="mock">Mock</option>
                      <option value="twilio">Twilio</option>
                    </select>
                  </div>
                  <div>
                    <Label>To (E.164)</Label>
                    <Input
                      data-testid="messaging-test-to"
                      placeholder="+14155551234"
                      value={toPhone} onChange={(e) => setToPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Event type</Label>
                    <select
                      data-testid="messaging-test-event-type"
                      className="w-full h-9 border rounded px-2 text-sm"
                      value={eventType} onChange={(e) => setEventType(e.target.value)}
                    >
                      <option value="order_placed">order_placed</option>
                      <option value="payment_received">payment_received</option>
                      <option value="label_created">label_created</option>
                      <option value="shipment_delivered">shipment_delivered</option>
                      <option value="lead_followup">lead_followup</option>
                      <option value="custom">custom</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea
                    data-testid="messaging-test-body"
                    rows={3} value={body} onChange={(e) => setBody(e.target.value)}
                  />
                </div>
                <Button
                  data-testid="messaging-send-test-button"
                  disabled={sending || !toPhone || !body}
                  onClick={sendTest}
                >{sending ? "Sending…" : "Send test SMS"}</Button>

                {lastResult && (
                  <pre
                    data-testid="messaging-test-result"
                    className="mt-3 text-[11px] bg-gray-50 border rounded p-3 whitespace-pre-wrap break-all"
                  >{JSON.stringify(lastResult, null, 2)}</pre>
                )}
              </div>
            </TabsContent>

            {/* ---------------- Logs ---------------- */}
            <TabsContent value="logs" className="space-y-3 pt-4">
              <Button size="sm" variant="outline" onClick={loadLogs} data-testid="messaging-logs-refresh">Refresh</Button>
              {loadingLogs ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : logs.length === 0 ? (
                <div className="text-sm text-gray-500">No SMS sends yet.</div>
              ) : (
                <div className="border rounded-lg overflow-hidden bg-white" data-testid="messaging-logs-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Provider</th>
                        <th className="px-3 py-2 text-left">Event</th>
                        <th className="px-3 py-2 text-left">To …</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">SID / Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l) => (
                        <tr key={l.id} className="border-t">
                          <td className="px-3 py-2 text-xs">{l.created_at?.slice(0, 19).replace("T", " ")}</td>
                          <td className="px-3 py-2">{l.provider}</td>
                          <td className="px-3 py-2 text-xs">{l.event_type || "—"}</td>
                          <td className="px-3 py-2 text-xs">…{l.to_phone_last4}</td>
                          <td className="px-3 py-2 text-xs">{l.status}</td>
                          <td className="px-3 py-2 text-xs">{l.provider_message_id || l.error_message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
